import Vue from 'vue';

/**
 * Appearance chosen in the profile menu: light, dark or follow the operating system.
 *
 * Stored in the browser (localStorage), per logged-in user, and – when the user-login-handler offers
 * `me/preferences` (Notify v2) – also at the user, so it follows to other devices (sync in
 * notificationPreferences.helper.ts: loaded after login, saved by the profile menu). localStorage stays the source for
 * the first paint; the last choice on this device is kept separately, so the theme is right before the login is known.
 */
export type ThemePreference = 'light' | 'dark' | 'system';

export const THEME_PREFERENCES: ThemePreference[] = ['light', 'dark', 'system'];

const KEY_PREFIX = 'vanilla.theme.';
/** Last choice on this device, used before the user is known (first paint, login page) */
const LAST_KEY = `${KEY_PREFIX}last`;
const DARK_QUERY = '(prefers-color-scheme: dark)';

const isThemePreference = (value: unknown): value is ThemePreference => THEME_PREFERENCES.includes(value as ThemePreference);

const userKey = (userId: unknown) => `${KEY_PREFIX}user.${String(userId)}`;

const readKey = (key: string): ThemePreference | undefined => {
    try {
        const value = localStorage.getItem(key);
        return isThemePreference(value) ? value : undefined;
    } catch (e) {
        return undefined;
    }
};

const writeKey = (key: string, value: ThemePreference) => {
    try {
        localStorage.setItem(key, value);
    } catch (e) {
        // Storage blocked (private mode, policy): the choice only lasts for this page view
    }
};

/** Choice of the given user, or the last choice on this device when no user (or no choice of the user) is known */
export const readThemePreference = (userId?: unknown): ThemePreference => {
    const own = userId ? readKey(userKey(userId)) : undefined;
    return own || readKey(LAST_KEY) || 'system';
};

export const writeThemePreference = (value: ThemePreference, userId?: unknown): void => {
    if (userId) {
        writeKey(userKey(userId), value);
    }
    writeKey(LAST_KEY, value);
};

export const systemPrefersDark = (): boolean => {
    try {
        return !!window.matchMedia && window.matchMedia(DARK_QUERY).matches;
    } catch (e) {
        return false;
    }
};

export const resolveDark = (preference: ThemePreference): boolean => {
    if (preference === 'system') {
        return systemPrefersDark();
    }
    return preference === 'dark';
};

/**
 * Calls the handler when the operating system switches between light and dark.
 * @returns cleanup function
 */
export const watchSystemTheme = (handler: (dark: boolean) => void): () => void => {
    if (!window.matchMedia) {
        return () => undefined;
    }
    const query = window.matchMedia(DARK_QUERY);
    const listener = (event: MediaQueryListEvent) => handler(event.matches);
    if (query.addEventListener) {
        query.addEventListener('change', listener);
        return () => query.removeEventListener('change', listener);
    }
    // Safari < 14
    query.addListener(listener);
    return () => query.removeListener(listener);
};

/** Reactive current choice, shared by the profile menu and the theme controller in App.vue */
export const themeState = Vue.observable({
    preference: readThemePreference() as ThemePreference,
});

/** Stores the choice of the user and applies it (App.vue watches the state) */
export const setThemePreference = (value: ThemePreference, userId?: unknown): void => {
    themeState.preference = value;
    writeThemePreference(value, userId);
};

/** Background of the page before Vuetify has rendered (matches the shell background) */
const PAGE_BACKGROUND = {light: '#F4F5F7', dark: '#121417'};

/** Sets color-scheme and background on <html>, so scrollbars, form controls and overscroll match the theme */
export const applyDocumentTheme = (dark: boolean): void => {
    try {
        const root = document.documentElement;
        root.style.colorScheme = dark ? 'dark' : 'light';
        root.style.backgroundColor = dark ? PAGE_BACKGROUND.dark : PAGE_BACKGROUND.light;
    } catch (e) {
        // no document (tests)
    }
};

export const THEME_OPTIONS: Array<{ value: ThemePreference; text: string; icon: string }> = [
    {value: 'light', text: 'Hell', icon: 'mdi-white-balance-sunny'},
    {value: 'dark', text: 'Dunkel', icon: 'mdi-weather-night'},
    {value: 'system', text: 'System', icon: 'mdi-laptop'},
];
